import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';

/**
 * Redux Store: Web-App-Landing
 **/

const initialState = {
  c1Accounts: {
    requestAccounts: false,
    hasError: false,
    c1AccountID: '',
    c1AccountsList: [],
    c1CloudProviders: {},
    fetchRecInvitations: false,
    invitationsReceived: [],
    // TODO: remove this state when v1->v2 migration is done
    // isCloudOneBacked: true to avoid showing banner or causing redirect initially
    cloudOneBackedStatus: {
      isCloudOneBacked: true,
      showInitialMigrationPage: true,
    },
    redirectToV1UpdateAccount: true, // set true to redirect full-access user to update-account page on sign in,
    c1CloudProviders: [],
  },
  c1Services: { fetchServices: false, servicesUpdated: false, hasError: false, c1ServicesList: [] },
  c1Token: { token: '', expires: '', hasError: false, fetchC1Token: false, serviceRoleUrnList: [] },
  c1UserDetails: {
    name: '',
    email: '',
    wsAdminID: '',
    type: '',
    hasError: false,
    hasMFAResetCode: null,
  },

  c1Common: {
    storeId: 'web-app-landing',
    wsLoggedIn: false,
    wsNavigationRestricted: false, // WS session state during rescue login sign-in phase
    loggedIn: false,
    loggingOut: false,
    isLoading: false,
    authDetails: { authType: '', expires: '' },
    appSelection: '',
    locale: 'en',
    errorMsg: '',
    theme: { colorMode: 'light', overrides: {} },
  },
};

export function reducer(state = initialState, action) {
  switch (action.type) {
    case 'STORE_INIT':
      // rehydrate the states base on shell state (e.g. upon refresh)
      return {
        ...action.shellInitState,
        c1Common: {
          ...action.shellInitState.c1Common,
          storeId: 'web-app-landing',
        },
      };
    case 'NAVIGATE_APP':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          appSelection: action.appName,
        },
      };
    case 'WS_LOGGED_IN':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          wsLoggedIn: true,
        },
      };
    case 'WS_RESCUE_LOGGED_IN':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          wsLoggedIn: true,
          wsNavigationRestricted: true,
        },
      };
    case 'WS_LOGGED_OUT':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          wsLoggedIn: false,
          wsNavigationRestricted: false,
        },
      };
    case 'LOGGED_IN':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          loggedIn: true,
          loggingOut: false,
        },
      };
    case 'LOGGING_OUT':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          loggedIn: false,
          loggingOut: true,
        },
      };
    case 'ADD_AUTH_DETAILS':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          authDetails: {
            ...state.authDetails,
            ...action.authDetails,
          },
        },
      };
    case 'SHOW_LOADING_INDICATOR':
    case 'HIDE_LOADING_INDICATOR': {
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          isLoading: action.loading,
        },
      };
    }
    case 'SET_LOCALE':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          locale: action.locale,
        },
      };
    case 'FETCH_SERVICES':
      return {
        ...state,
        c1Services: {
          ...state.c1Services,
          fetchServices: action.fetchServices,
        },
      };
    case 'STORE_SERVICES':
      return {
        ...state,
        c1Services: {
          ...state.c1Services,
          c1ServicesList: action.c1ServicesList,
          hasError: false,
          errorMsg: '',
          fetchServices: false,
          servicesUpdated: true,
        },
        c1Common: {
          ...state.c1Common,
          isLoading: false,
        },
      };
    case 'FETCH_C1_TOKEN':
      return {
        ...state,
        c1Token: {
          ...action.c1Token,
        },
        c1Common: {
          ...state.c1Common,
          isLoading: action.fetchC1Token,
        },
      };
    case 'STORE_C1_TOKEN':
      return {
        ...state,
        c1Token: {
          ...action.c1Token,
          hasError: false,
          fetchC1Token: false,
          errorMsg: '',
        },
      };
    case 'ADD_LAST_SELECTED_ACCOUNT_ID':
    case 'ADD_ACCOUNT_ID':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          lastAccessedAccountID: state.c1Accounts.c1AccountID,
          c1AccountID: action.c1AccountID,
        },
        c1Services: {
          ...state.c1Services,
          servicesUpdated: false,
        },
      };
    case 'STORE_ACCOUNTS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountsList: action.storeAccounts,
          requestAccounts: false,
        },
      };
    case 'FETCH_RECEIVED_INVITATIONS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          fetchRecInvitations: action.fetchRecInvitations,
        },
      };
    case 'STORE_RECEIVED_INVITATIONS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          hasError: false,
          fetchRecInvitations: false,
          invitationsReceived: action.invitationsReceived,
        },
      };
    case 'SET_THEME':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: action.theme,
        },
      };
    case 'SET_COLOR_MODE':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: {
            ...state.c1Common.theme,
            colorMode: action.colorMode,
          },
        },
      };
    case 'SET_THEME_OVERRIDES':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: {
            ...state.c1Common.theme,
            overrides: action.overrides,
          },
        },
      };
    case 'SET_WS_ADMIN_ID':
      return {
        ...state,
        c1UserDetails: { ...state.c1UserDetails, wsAdminID: action.wsAdminID },
      };
    case 'ADD_WS_USER_INFO': {
      const {
        fullName = '',
        emailAddress = '',
        timeZone = '',
        phoneNumber = '',
        mobileNumber = '',
      } = action.userDetails;
      return {
        ...state,
        c1UserDetails: {
          ...state.c1UserDetails,
          wsFullName: fullName,
          wsEmailAddress: emailAddress,
          wsTimeZone: timeZone,
          wsPhoneNumber: phoneNumber,
          wsMobileNumber: mobileNumber,
        },
      };
    }
    case 'ADD_WS_USER_EMAIL':
      return {
        ...state,
        c1UserDetails: {
          ...state.c1UserDetails,
          wsEmailAddress: action.email,
        },
      };
    case 'ADD_USER_TYPE':
      return {
        ...state,
        c1UserDetails: {
          ...state.c1UserDetails,
          type: action.userType,
        },
      };
    case 'ADD_USER_INFO': {
      const { name = '', email = '' } = action.userDetails;
      return {
        ...state,
        c1UserDetails: {
          ...state.c1UserDetails,
          name,
          email,
        },
      };
    }
    case 'HYDRATE_STORE':
      return {
        ...state,
        ...action.storeState,
      };
    case 'RESET_STORE':
      return {
        ...state,
        ...initialState,
      };
    case 'ERROR_FETCHING_C1_TOKEN':
      return {
        ...state,
        c1Token: {
          ...state.c1Token,
          fetchC1Token: false,
          hasError: true,
          errorMsg: action.errorMsg,
        },
      };
    case 'ERROR_FETCHING_ACCOUNTS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          requestAccounts: false,
          fetchRecInvitations: false,
          hasError: true,
        },
      };
    case 'ERROR_FETCHING_SERVICES':
      return {
        ...state,
        c1Services: {
          ...state.c1Services,
          fetchServices: false,
          hasError: true,
        },
      };
    case 'ADD_CLOUDONE_BACKED_STATUS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          cloudOneBackedStatus: {
            ...state.c1Accounts.cloudOneBackedStatus,
            isCloudOneBacked: action.isCloudOneBacked,
            showInitialMigrationPage: action.showInitialMigrationPage,
          },
        },
      };
    case 'RESET_CLOUDONE_BACKED_STATUS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          cloudOneBackedStatus: {
            ...initialState.c1Accounts.cloudOneBackedStatus,
          },
        },
      };
    case 'TOGGLE_SHOW_INITIAL_MIGRATION_PAGE':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          cloudOneBackedStatus: {
            ...state.c1Accounts.cloudOneBackedStatus,
            showInitialMigrationPage: action.showInitialMigrationPage,
          },
        },
      };
    case 'TOGGLE_REDIRECT_TO_V1_UPDATE_ACCOUNT_PAGE':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          redirectToV1UpdateAccount: action.redirectToV1UpdateAccount,
        },
      };
    case 'TOGGLE_HAS_MFA_RESET_CODE':
      return {
        ...state,
        c1UserDetails: {
          ...state.c1UserDetails,
          hasMFAResetCode: action.hasMFAResetCode,
        },
      };
    case 'UPDATE_CLOUD_PROVIDER_ACCOUNTS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1CloudProviders: {
            ...state.c1Accounts.c1CloudProviders,
            [action.provider]: action.accounts,
          },
        },
      };
    default:
      return state;
  }
}

export const getServices = (state, type) => state?.c1Services[type];
export const getUserDetails = (state, type) => state?.c1UserDetails[type];
export const getAccounts = (state, type) => state?.c1Accounts[type];
export const getToken = (state, type) => state?.c1Token[type];
export const getCloudProviders = (state) => state?.c1Accounts?.c1CloudProviders;

export const getCommonType = (state = {}, key = '') => {
  const c1CommonState = state?.c1Common;
  if (c1CommonState[key] !== undefined) {
    return c1CommonState[key];
  } else {
    for (const objProp in c1CommonState) {
      if (c1CommonState[objProp] && c1CommonState[objProp][key]) {
        return c1CommonState[objProp][key];
      }
    }
  }
};

export const getAppName = (state) => {
  const appName = state?.c1Common?.appSelection;
  if (appName && appName !== 'home') {
    return ('/' + appName).split('/').slice(1, 2).join('/');
  } else {
    return window.location.pathname.split('/').slice(1, 2).join('/');
  }
};

export const navigateApp = (appName) => ({
  type: 'NAVIGATE_APP',
  appName,
});

export const getCommonError = (state) =>
  state?.c1Services?.hasError || state?.c1Accounts?.hasError || state?.c1Token?.hasError || false;

export const getAccountInfo = (state, key = '') => {
  const currentAccountID = getAccounts(state, 'c1AccountID');
  const accountInfo = state?.c1Accounts?.c1AccountsList.find(
    (account) => account.id === currentAccountID
  );
  return accountInfo ? (key === '' ? accountInfo : accountInfo[key]) : '';
};

export const getSecurityServices = (state, categoryType) => {
  const services = getServices(state, 'c1ServicesList');
  return services.filter(({ category }) => category === categoryType);
};

/**
 * Get role
 * Camel Case used for display role specific strings
 * */
export const getRole = (state, isCamelCase) => {
  const role = getToken(state, 'role')?.split('/')[1];
  return role ? (isCamelCase ? role.replace(/-([a-z])/g, (g) => g[1].toUpperCase()) : role) : '';
};

/**
 * Custom Logger Middleware
 * @param store
 * @returns {function(*): function(*=): *}
 */
/**
 * Logs all actions and states after they are dispatched.
 */
const logger = (store) => (next) => (action) => {
  console.group(action.type, 'web-app-landing');
  console.debug('web-app-landing dispatching', action);

  // Call the next dispatch method in the middleware chain
  const result = next(action);

  console.debug('web-app-landing next state after dispatch', store.getState());
  console.groupEnd();
  return result;
};

const composeEnhancers = composeWithDevTools({ name: 'web-app-landing' });
export const storeInstance = createStore(reducer, composeEnhancers(applyMiddleware(logger)));
